import { z } from 'zod/v4';
import { SAFE_INTERNAL_PATH_PATTERN } from '../utils/aiContent/safeInternalPath';
import { AI_DATA_FIELD_REFERENCE_ID_PATTERN } from './aiStructuredContext';
import { ChartSourceType } from './content';
import { MarkdownChartType, type MarkdownSeriesChartType } from './markdown';

export enum AiContentFormat {
    LEGACY_MARKDOWN = 'legacy-markdown',
    OPENUI_V1 = 'openui-v1',
}

export enum AiContentSurface {
    CHAT = 'chat',
    TASK_REPORT = 'task-report',
    HOME_FEED = 'home-feed',
    ADMIN_CHAT = 'admin-chat',
    USER_MESSAGE = 'user-message',
    QUEST_OVERVIEW = 'quest-overview',
    QUEST_PLAN_ITEM = 'quest-plan-item',
}

export enum AiContentVariant {
    DARK = 'dark',
    LIGHT = 'light',
}

export enum AiContentChannel {
    MARKDOWN = 'markdown',
    SLACK_BLOCK_KIT = 'slack-block-kit',
}

export enum OpenUiComponentName {
    RESPONSE = 'Response',
    MARKDOWN = 'Markdown',
    METRIC = 'Metric',
    CALLOUT = 'Callout',
    DATA_TABLE = 'DataTable',
    CHART = 'Chart',
    SQL_RUNNER = 'SqlRunner',
    CLARIFICATION = 'Clarification',
    CONNECTION_PICKER = 'ConnectionPicker',
    ACCORDION = 'Accordion',
    CODE = 'Code',
    PRODUCT_LINK = 'ProductLink',
    SCHEDULE_LINK = 'ScheduleLink',
    ATTACHMENT = 'Attachment',
    TASK_REPORT = 'TaskReport',
    HOME_FEED_ITEM = 'HomeFeedItem',
    TABLE_REFERENCE = 'TableReference',
    COLUMN_REFERENCE = 'ColumnReference',
    AUDIENCE = 'Audience',
    CAMPAIGN = 'Campaign',
    CUSTOM_ATTRIBUTE = 'CustomAttribute',
    JOURNEY = 'Journey',
    TEMPLATE = 'Template',
    INTELLIGENCE_BLOCK = 'IntelligenceBlock',
    TASK = 'Task',
    QUEST = 'Quest',
    MEDIA_ASSET = 'MediaAsset',
    KPI = 'KPI',
    KPI_CARD = 'KpiCard',
    QUEST_OVERVIEW_SUMMARY = 'QuestOverviewSummary',
    SAVED_CHART = 'SavedChart',
    DASHBOARD = 'Dashboard',
    REFERENCES = 'References',
}

export enum OpenUiMetricTrend {
    UP = 'UP',
    DOWN = 'DOWN',
    NEUTRAL = 'NEUTRAL',
}

export enum OpenUiCalloutTone {
    INFO = 'INFO',
    SUCCESS = 'SUCCESS',
    WARNING = 'WARNING',
}

export enum OpenUiChartType {
    LINE = 'LINE',
    BAR = 'BAR',
    AREA = 'AREA',
    SCATTER = 'SCATTER',
    HORIZONTAL_BAR = 'HORIZONTAL_BAR',
    PIE = 'PIE',
    DONUT = 'DONUT',
    COMBO = 'COMBO',
}

export enum OpenUiChartSourceType {
    DBT_EXPLORE = 'DBT_EXPLORE',
    SQL = 'SQL',
}

export const SORTMENT_OPENUI_LIBRARY_ID = 'sortment-ai-v1';
export const OPENUI_RESPONSE_COMPONENT_DESCRIPTION =
    'Invisible root for every response. Put each visible block in children in display order.';
export const OPENUI_REFERENCES_COMPONENT_DESCRIPTION =
    'A collapsible group of verified Sortment references.';
export const OPENUI_RESPONSE_MIN_CHILDREN = 1;
export const OPENUI_RESPONSE_MAX_CHILDREN = 100;
export const OPENUI_REFERENCES_MIN_CHILDREN = 1;
export const OPENUI_REFERENCES_MAX_CHILDREN = 30;
export const OPENUI_QUEST_OVERVIEW_MAX_KPIS = 4;

const nonEmptyString = z.string().min(1);

const normalizeOpenUiEnum = <T extends string>(
    value: unknown,
    values: Readonly<Record<string, T>>,
): T | undefined => {
    if (typeof value !== 'string') return undefined;
    return values[value] ?? values[value.toUpperCase()];
};

export const normalizeOpenUiMetricTrend = (
    value: unknown,
): OpenUiMetricTrend | undefined =>
    normalizeOpenUiEnum(value, OpenUiMetricTrend);

export const normalizeOpenUiCalloutTone = (
    value: unknown,
): OpenUiCalloutTone | undefined =>
    normalizeOpenUiEnum(value, OpenUiCalloutTone);

export const normalizeOpenUiChartType = (
    value: unknown,
): OpenUiChartType | undefined => {
    if (value === MarkdownChartType.HORIZONTAL_BAR) {
        return OpenUiChartType.HORIZONTAL_BAR;
    }
    return normalizeOpenUiEnum(value, OpenUiChartType);
};

export const getMarkdownChartTypeFromOpenUi = (
    value: unknown,
): MarkdownChartType | undefined => {
    switch (normalizeOpenUiChartType(value)) {
        case OpenUiChartType.LINE:
            return MarkdownChartType.LINE;
        case OpenUiChartType.BAR:
            return MarkdownChartType.BAR;
        case OpenUiChartType.AREA:
            return MarkdownChartType.AREA;
        case OpenUiChartType.SCATTER:
            return MarkdownChartType.SCATTER;
        case OpenUiChartType.HORIZONTAL_BAR:
            return MarkdownChartType.HORIZONTAL_BAR;
        case OpenUiChartType.PIE:
            return MarkdownChartType.PIE;
        case OpenUiChartType.DONUT:
            return MarkdownChartType.DONUT;
        case OpenUiChartType.COMBO:
            return MarkdownChartType.COMBO;
        default:
            return undefined;
    }
};

export const getMarkdownSeriesChartTypeFromOpenUi = (
    value: unknown,
): MarkdownSeriesChartType | undefined => {
    switch (normalizeOpenUiChartType(value)) {
        case OpenUiChartType.LINE:
            return MarkdownChartType.LINE;
        case OpenUiChartType.BAR:
            return MarkdownChartType.BAR;
        case OpenUiChartType.AREA:
            return MarkdownChartType.AREA;
        case OpenUiChartType.SCATTER:
            return MarkdownChartType.SCATTER;
        default:
            return undefined;
    }
};

export const normalizeOpenUiChartSourceType = (
    value: unknown,
): OpenUiChartSourceType | undefined => {
    if (value === ChartSourceType.DBT_EXPLORE) {
        return OpenUiChartSourceType.DBT_EXPLORE;
    }
    return normalizeOpenUiEnum(value, OpenUiChartSourceType);
};

export const getChartSourceTypeFromOpenUi = (
    value: unknown,
): ChartSourceType | undefined => {
    switch (normalizeOpenUiChartSourceType(value)) {
        case OpenUiChartSourceType.DBT_EXPLORE:
            return ChartSourceType.DBT_EXPLORE;
        case OpenUiChartSourceType.SQL:
            return ChartSourceType.SQL;
        default:
            return undefined;
    }
};

const normalizeKnownProp = (
    props: Record<string, unknown>,
    key: string,
    normalize: (value: unknown) => string | undefined,
) => {
    const normalized = normalize(props[key]);
    return normalized === undefined ? props : { ...props, [key]: normalized };
};

export const normalizeOpenUiComponentProps = (
    componentName: string,
    props: Record<string, unknown>,
): Record<string, unknown> => {
    switch (componentName) {
        case OpenUiComponentName.METRIC:
            return normalizeKnownProp(
                props,
                'trend',
                normalizeOpenUiMetricTrend,
            );
        case OpenUiComponentName.CALLOUT:
            return normalizeKnownProp(
                props,
                'tone',
                normalizeOpenUiCalloutTone,
            );
        case OpenUiComponentName.CHART: {
            const normalized = normalizeKnownProp(
                props,
                'chartType',
                normalizeOpenUiChartType,
            );
            if (!Array.isArray(normalized.series)) return normalized;
            return {
                ...normalized,
                series: normalized.series.map((series) => {
                    if (typeof series !== 'object' || series === null) {
                        return series;
                    }
                    return normalizeKnownProp(
                        series as Record<string, unknown>,
                        'type',
                        normalizeOpenUiChartType,
                    );
                }),
            };
        }
        case OpenUiComponentName.SQL_RUNNER:
            return normalizeKnownProp(
                props,
                'chartKind',
                normalizeOpenUiChartType,
            );
        case OpenUiComponentName.SAVED_CHART:
            return normalizeKnownProp(
                props,
                'source',
                normalizeOpenUiChartSourceType,
            );
        default:
            return props;
    }
};

export const openUiMarkdownPropsSchema = z.object({ content: nonEmptyString });
export const openUiMetricPropsSchema = z.object({
    label: nonEmptyString,
    value: nonEmptyString,
    change: z.string().optional(),
    trend: z.nativeEnum(OpenUiMetricTrend).optional(),
});
export const openUiCalloutPropsSchema = z.object({
    title: nonEmptyString,
    body: nonEmptyString,
    tone: z.nativeEnum(OpenUiCalloutTone).optional(),
});
export const openUiDataTablePropsSchema = z
    .object({
        headers: z.array(z.string()).min(1).max(12),
        rows: z.array(z.array(z.string()).max(12)).max(100),
        caption: z.string().optional(),
    })
    .superRefine((table, refinement) => {
        table.rows.forEach((row, index) => {
            if (row.length !== table.headers.length) {
                refinement.addIssue({
                    code: 'custom',
                    path: ['rows', index],
                    message: 'Each table row must match the header count.',
                });
            }
        });
    });
const chartSeriesSchema = z.object({
    name: nonEmptyString,
    values: z.array(z.number().finite()).min(1).max(50),
    type: z
        .enum([
            OpenUiChartType.LINE,
            OpenUiChartType.BAR,
            OpenUiChartType.AREA,
            OpenUiChartType.SCATTER,
        ])
        .optional(),
});
const chartSliceSchema = z.object({
    name: nonEmptyString,
    value: z.number().finite(),
});
export const openUiChartPropsSchema = z
    .object({
        title: nonEmptyString,
        chartType: z.nativeEnum(OpenUiChartType),
        xAxis: z.array(nonEmptyString).min(1).max(50).optional(),
        yAxisLabel: z.string().optional(),
        yAxisMin: z.number().finite().optional(),
        yAxisMax: z.number().finite().optional(),
        series: z.array(chartSeriesSchema).min(1).max(5).optional(),
        slices: z.array(chartSliceSchema).min(1).max(12).optional(),
        valueLabel: z.string().optional(),
    })
    .superRefine((chart, context) => {
        if (
            chart.yAxisMin !== undefined &&
            chart.yAxisMax !== undefined &&
            chart.yAxisMin >= chart.yAxisMax
        ) {
            context.addIssue({
                code: 'custom',
                path: ['yAxisMax'],
                message: 'yAxisMax must be greater than yAxisMin.',
            });
        }

        const isSlice =
            chart.chartType === OpenUiChartType.PIE ||
            chart.chartType === OpenUiChartType.DONUT;

        if (isSlice) {
            if (!chart.slices?.length) {
                context.addIssue({
                    code: 'custom',
                    path: ['slices'],
                    message: 'Pie and donut charts require slices.',
                });
            }

            const sliceNames = new Set<string>();
            chart.slices?.forEach((slice, index) => {
                if (sliceNames.has(slice.name)) {
                    context.addIssue({
                        code: 'custom',
                        path: ['slices', index, 'name'],
                        message: 'Slice names must be unique.',
                    });
                }
                sliceNames.add(slice.name);
            });
            return;
        }

        if (!chart.xAxis?.length || !chart.series?.length) {
            context.addIssue({
                code: 'custom',
                path: ['series'],
                message: 'Cartesian charts require xAxis and series.',
            });
            return;
        }

        const seriesNames = new Set<string>();
        chart.series.forEach((series, index) => {
            if (series.name === 'label') {
                context.addIssue({
                    code: 'custom',
                    path: ['series', index, 'name'],
                    message: 'The series name "label" is reserved.',
                });
            }
            if (seriesNames.has(series.name)) {
                context.addIssue({
                    code: 'custom',
                    path: ['series', index, 'name'],
                    message: 'Series names must be unique.',
                });
            }
            seriesNames.add(series.name);

            if (series.values.length !== chart.xAxis?.length) {
                context.addIssue({
                    code: 'custom',
                    path: ['series', index, 'values'],
                    message: 'Series values must align with xAxis categories.',
                });
            }
        });
    });
export const openUiSqlRunnerPropsSchema = z.object({
    label: nonEmptyString,
    sql: nonEmptyString,
    chartKind: z.nativeEnum(OpenUiChartType).optional(),
});
export const openUiClarificationOptionSchema = z.object({
    id: nonEmptyString,
    label: nonEmptyString,
    description: z.string().optional(),
    recommended: z.boolean().optional(),
});
export const openUiConnectionPickerPropsSchema = z.object({
    title: nonEmptyString,
    appKey: nonEmptyString.optional(),
});
export const openUiClarificationQuestionSchema = z
    .object({
        id: nonEmptyString,
        question: nonEmptyString,
        options: z.array(openUiClarificationOptionSchema).min(2).max(5),
        allowCustomAnswer: z.literal(true).default(true),
        customAnswerLabel: z.string().optional(),
    })
    .superRefine((question, refinement) => {
        const optionIds = new Set<string>();

        question.options.forEach((option, index) => {
            if (optionIds.has(option.id)) {
                refinement.addIssue({
                    code: 'custom',
                    path: ['options', index, 'id'],
                    message: 'Clarification option IDs must be unique.',
                });
            }
            optionIds.add(option.id);
        });
    });
export const openUiClarificationPropsSchema = z
    .object({
        header: nonEmptyString,
        questions: z.array(openUiClarificationQuestionSchema).min(1).max(3),
    })
    .superRefine((clarification, refinement) => {
        const questionIds = new Set<string>();

        clarification.questions.forEach((question, index) => {
            if (questionIds.has(question.id)) {
                refinement.addIssue({
                    code: 'custom',
                    path: ['questions', index, 'id'],
                    message: 'Clarification question IDs must be unique.',
                });
            }
            questionIds.add(question.id);
        });
    });
export const openUiAccordionPropsSchema = z.object({
    title: nonEmptyString,
    content: nonEmptyString,
    defaultExpanded: z.boolean().optional(),
});
export const openUiCodePropsSchema = z.object({
    code: nonEmptyString,
    language: z.string().optional(),
});
export const openUiProductLinkPropsSchema = z.object({
    label: nonEmptyString,
    path: z.string().regex(SAFE_INTERNAL_PATH_PATTERN),
});
export const openUiScheduleLinkPropsSchema = z.object({
    label: z.string().optional(),
});
export const openUiAttachmentPropsSchema = z.object({
    id: nonEmptyString,
    label: nonEmptyString,
});
export const openUiTaskReportPropsSchema = z.object({
    taskId: nonEmptyString,
    insightId: nonEmptyString,
    label: nonEmptyString,
});
export const openUiHomeFeedItemPropsSchema = z.object({
    itemId: nonEmptyString,
    label: nonEmptyString,
});
export const openUiTableReferencePropsSchema = z.object({
    tableId: nonEmptyString,
    label: nonEmptyString,
    relationId: z.string().optional(),
});
export const openUiColumnReferencePropsSchema = z.object({
    columnId: z
        .string()
        .regex(AI_DATA_FIELD_REFERENCE_ID_PATTERN)
        .describe('Canonical table.column identifier.'),
    label: nonEmptyString,
    relationId: z.string().optional(),
});

const entityReferenceBaseSchema = z.object({
    id: nonEmptyString,
    label: nonEmptyString,
});
export const openUiAudiencePropsSchema = entityReferenceBaseSchema.extend({});
export const openUiCampaignPropsSchema = entityReferenceBaseSchema.extend({});
export const openUiCustomAttributePropsSchema =
    entityReferenceBaseSchema.extend({});
export const openUiJourneyPropsSchema = entityReferenceBaseSchema.extend({});
export const openUiTemplatePropsSchema = entityReferenceBaseSchema.extend({});
export const openUiIntelligenceBlockPropsSchema =
    entityReferenceBaseSchema.extend({});
export const openUiTaskPropsSchema = entityReferenceBaseSchema.extend({});
export const openUiQuestPropsSchema = entityReferenceBaseSchema.extend({});
export const openUiMediaAssetPropsSchema = entityReferenceBaseSchema.extend({});
export const openUiKpiPropsSchema = entityReferenceBaseSchema.extend({
    relationId: z.string().optional(),
});
export const openUiKpiCardPropsSchema = entityReferenceBaseSchema.extend({});
export const openUiQuestOverviewSummaryPropsSchema = z
    .object({
        originalObjective: nonEmptyString.describe(
            'The approved objective the Quest is working toward, written as concise plain text.',
        ),
        currentState: nonEmptyString.describe(
            'The latest decision-relevant state of the Quest, written as concise Markdown without a heading.',
        ),
        kpis: z
            .array(openUiKpiCardPropsSchema)
            .max(OPENUI_QUEST_OVERVIEW_MAX_KPIS)
            .optional()
            .describe(
                'Verified live KPI references that belong in the Quest Overview. Omit when the Quest has no dedicated KPI.',
            ),
        contextMarkdown: z
            .string()
            .optional()
            .describe(
                'Planner-maintained public context: the original conversation, files, links, definitions, constraints, decisions and subsequent human inputs as readable Markdown. Preserve source references and corrections; exclude private reasoning and credentials.',
            ),
    })
    .superRefine((overview, refinement) => {
        const kpiIds = new Set<string>();
        overview.kpis?.forEach((kpi, index) => {
            if (kpiIds.has(kpi.id)) {
                refinement.addIssue({
                    code: 'custom',
                    path: ['kpis', index, 'id'],
                    message: 'Quest Overview KPI IDs must be unique.',
                });
            }
            kpiIds.add(kpi.id);
        });
    });
export const openUiSavedChartPropsSchema = entityReferenceBaseSchema.extend({
    source: z.nativeEnum(OpenUiChartSourceType),
    slug: z.string().optional(),
    spaceName: z.string().optional(),
    chartKind: z.string().optional(),
});
export const openUiDashboardPropsSchema = entityReferenceBaseSchema.extend({
    spaceName: z.string().optional(),
});

export const OPENUI_REFERENCE_COMPONENT_NAMES = [
    OpenUiComponentName.ATTACHMENT,
    OpenUiComponentName.TASK_REPORT,
    OpenUiComponentName.HOME_FEED_ITEM,
    OpenUiComponentName.TABLE_REFERENCE,
    OpenUiComponentName.COLUMN_REFERENCE,
    OpenUiComponentName.AUDIENCE,
    OpenUiComponentName.CAMPAIGN,
    OpenUiComponentName.CUSTOM_ATTRIBUTE,
    OpenUiComponentName.JOURNEY,
    OpenUiComponentName.TEMPLATE,
    OpenUiComponentName.INTELLIGENCE_BLOCK,
    OpenUiComponentName.TASK,
    OpenUiComponentName.QUEST,
    OpenUiComponentName.MEDIA_ASSET,
    OpenUiComponentName.KPI,
    OpenUiComponentName.SAVED_CHART,
    OpenUiComponentName.DASHBOARD,
] as const;

export const createOpenUiReferencesPropsSchema = (
    referenceSchemas: [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]],
) =>
    z.object({
        children: z
            .array(z.union(referenceSchemas))
            .min(OPENUI_REFERENCES_MIN_CHILDREN)
            .max(OPENUI_REFERENCES_MAX_CHILDREN),
    });

export const createOpenUiResponsePropsSchema = (
    childSchemas: [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]],
) =>
    z.object({
        children: z
            .array(z.union(childSchemas))
            .min(OPENUI_RESPONSE_MIN_CHILDREN)
            .max(OPENUI_RESPONSE_MAX_CHILDREN),
    });

export const openUiComponentContract = {
    [OpenUiComponentName.MARKDOWN]: [
        'Safe prose, lists, headings, links, and Markdown tables.',
        openUiMarkdownPropsSchema,
    ],
    [OpenUiComponentName.METRIC]: [
        'A compact headline number.',
        openUiMetricPropsSchema,
    ],
    [OpenUiComponentName.CALLOUT]: [
        'A highlighted finding or recommendation.',
        openUiCalloutPropsSchema,
    ],
    [OpenUiComponentName.DATA_TABLE]: [
        'A compact table of verified values.',
        openUiDataTablePropsSchema,
    ],
    [OpenUiComponentName.CHART]: [
        'A transient chart built from verified values.',
        openUiChartPropsSchema,
    ],
    [OpenUiComponentName.SQL_RUNNER]: [
        'Preview executable SQL in Sortment SQL Runner. Label must be the concise query name.',
        openUiSqlRunnerPropsSchema,
    ],
    [OpenUiComponentName.CLARIFICATION]: [
        'A controlled clarification or approval question.',
        openUiClarificationPropsSchema,
    ],
    [OpenUiComponentName.CONNECTION_PICKER]: [
        'A terminal connection/account choice for a confirmed data source. Pass the verified connector appKey; omit it only to choose among custom MCP servers. Fetches the current user-visible connections itself, including disabled unavailable rows. Do not provide connection IDs, options, logos or statuses. Use only when a specific connection choice is needed, not for general data-source questions or approvals.',
        openUiConnectionPickerPropsSchema,
    ],
    [OpenUiComponentName.ACCORDION]: [
        'Expandable supplementary Markdown.',
        openUiAccordionPropsSchema,
    ],
    [OpenUiComponentName.CODE]: [
        'A read-only code block.',
        openUiCodePropsSchema,
    ],
    [OpenUiComponentName.PRODUCT_LINK]: [
        'A safe internal Sortment link.',
        openUiProductLinkPropsSchema,
    ],
    [OpenUiComponentName.SCHEDULE_LINK]: [
        'Open Sync schedules without creating one.',
        openUiScheduleLinkPropsSchema,
    ],
    [OpenUiComponentName.ATTACHMENT]: [
        'A reference to an existing chat attachment.',
        openUiAttachmentPropsSchema,
    ],
    [OpenUiComponentName.TASK_REPORT]: [
        'Open a completed Task Report.',
        openUiTaskReportPropsSchema,
    ],
    [OpenUiComponentName.HOME_FEED_ITEM]: [
        'Open an existing Home Feed item.',
        openUiHomeFeedItemPropsSchema,
    ],
    [OpenUiComponentName.TABLE_REFERENCE]: [
        'A verified relation table reference.',
        openUiTableReferencePropsSchema,
    ],
    [OpenUiComponentName.COLUMN_REFERENCE]: [
        'A verified table column reference.',
        openUiColumnReferencePropsSchema,
    ],
    [OpenUiComponentName.AUDIENCE]: [
        'A Sortment audience reference.',
        openUiAudiencePropsSchema,
    ],
    [OpenUiComponentName.CAMPAIGN]: [
        'A Sortment campaign reference.',
        openUiCampaignPropsSchema,
    ],
    [OpenUiComponentName.CUSTOM_ATTRIBUTE]: [
        'A Sortment custom attribute reference.',
        openUiCustomAttributePropsSchema,
    ],
    [OpenUiComponentName.JOURNEY]: [
        'A Sortment journey reference.',
        openUiJourneyPropsSchema,
    ],
    [OpenUiComponentName.TEMPLATE]: [
        'A Sortment template reference.',
        openUiTemplatePropsSchema,
    ],
    [OpenUiComponentName.INTELLIGENCE_BLOCK]: [
        'A Sortment Intelligence block reference.',
        openUiIntelligenceBlockPropsSchema,
    ],
    [OpenUiComponentName.TASK]: [
        'A Sortment Task reference.',
        openUiTaskPropsSchema,
    ],
    [OpenUiComponentName.QUEST]: [
        'A Sortment Quest reference.',
        openUiQuestPropsSchema,
    ],
    [OpenUiComponentName.MEDIA_ASSET]: [
        'A Sortment media-library asset reference.',
        openUiMediaAssetPropsSchema,
    ],
    [OpenUiComponentName.KPI]: [
        'A Sortment KPI reference.',
        openUiKpiPropsSchema,
    ],
    [OpenUiComponentName.KPI_CARD]: [
        'A live primary KPI card. Use the KPI UUID as id and its concise display name as label.',
        openUiKpiCardPropsSchema,
    ],
    [OpenUiComponentName.QUEST_OVERVIEW_SUMMARY]: [
        'The fixed-layout executive summary for a Quest Overview. Provide the approved original objective, the latest decision-relevant current state, and at most four verified KPI references. Never include human-input questions; the product renders those programmatically.',
        openUiQuestOverviewSummaryPropsSchema,
    ],
    [OpenUiComponentName.SAVED_CHART]: [
        'A saved Sortment chart reference.',
        openUiSavedChartPropsSchema,
    ],
    [OpenUiComponentName.DASHBOARD]: [
        'A saved Sortment dashboard reference.',
        openUiDashboardPropsSchema,
    ],
} as const;
